import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { LecturerComment } from '../entities/lecturerComments.js';
import { Permission } from '../enums/roles.js';

export const subscribeLecturerCommentsByLecturerIDAndInstitutionID = registerSubscription({
  operationName: 'subscribeLecturerCommentsByLecturerIDAndInstitutionID',
  type: () => [LecturerComment],
  params: [
    { name: 'lecturerID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, params.institutionID),
    ),
  ],
  resolve: (lecturerID: string, institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<LecturerComment>) => {
      return watchQuery<LecturerComment>(LecturerComment._collectionName, { institutionID, lecturerID });
    };
  },
});

export const createLecturerComment = registerMutation({
  operationName: 'createLecturerComment',
  resolve: (text: string, lecturerID: string, institutionID: string) => async () => {
    const currUser = await getCurrentUserServer();

    if (!context.serverContext && !currUser?.hasPermission(Permission.MANAGE_USER))
      throw new Error('No permission to add comments on lecturers');

    const newComment = {
      text: text,
      lecturerID: lecturerID,
      institutionID: institutionID,
      createdAt: new Date(),
      createdBy: currUser?._id,
    };

    const addCommentToCollection = await new LecturerComment(newComment).create();

    if (addCommentToCollection) return true;
    throw new Error(
      `Could not add new comment (text: ${text}; lecturerID: ${lecturerID}; institutionID: ${institutionID})`,
    );
  },
  type: () => Boolean,
  params: [
    { name: 'text', type: () => String },
    { name: 'lecturerID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  options: { allowHTTP: true },
});

export const deleteLecturerCommentByID = registerMutation({
  operationName: 'deleteLecturerCommentByID',
  resolve: (lecturerCommentID: string) => async () => {
    console.log('deleteLecturerCommentByID');
    const currUser = await getCurrentUserServer();
    if (!context.serverContext && !currUser?.hasPermission(Permission.MANAGE_USER))
      throw new Error('No permission to delete comments on lecturers');

    const removeResult = await new LecturerComment({ _id: lecturerCommentID }).remove();

    if (removeResult) return true;
    throw new Error(`Could not delete comment with id: ${lecturerCommentID}`);
  },
  type: () => Boolean,
  params: [{ name: 'lecturerCommentID', type: () => String }],
  options: { allowHTTP: true },
});
